import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { loadPairedContactsMirror } from '../pairing/pairedContactsPersistence.ts'
import {
  collectPeerHintsFromPushData,
  isHexSessionId,
  peerKeyVariants,
} from '../notifications/notifyPeerHints.ts'
import {
  contactDisplayName,
  listContacts,
  type ZionContact,
} from './pairingStore.ts'

const DB_NAME = 'zion_contacts_db'
const PAIRING_DB_NAME = 'zion-pairing'
const NOTIFY_NAMES_CACHE = 'zion-notify-names'
const NOTIFY_NAMES_PATH = '/app/__zion_notify_names.json'
const DB_VERSION = 1
const STORE = 'contacts'

interface ContactNameRow {
  /** peerId / sessionToken / nickname (lowercase) */
  id: string
  name: string
  customName?: string
  nickname?: string
  sessionToken?: string
  username: string
  updatedAt: number
}

interface ContactsDb extends DBSchema {
  contacts: {
    key: string
    value: ContactNameRow
  }
}

interface PairingContactsDb extends DBSchema {
  contacts: {
    key: string
    value: ZionContact
  }
}

let dbPromise: Promise<IDBPDatabase<ContactsDb>> | null = null
let pairingDbPromise: Promise<IDBPDatabase<PairingContactsDb>> | null = null

function getDb(): Promise<IDBPDatabase<ContactsDb>> {
  if (!dbPromise) {
    dbPromise = openDB<ContactsDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id' })
        }
      },
    })
  }
  return dbPromise
}

function getPairingDb(): Promise<IDBPDatabase<PairingContactsDb>> {
  if (!pairingDbPromise) {
    pairingDbPromise = openDB<PairingContactsDb>(PAIRING_DB_NAME, 1)
  }
  return pairingDbPromise
}

function displayForContact(c: ZionContact): string | null {
  const display = contactDisplayName(c).trim()
  if (!display || isHexSessionId(display) || display === 'Contatto') return null
  return display
}

function keysForContact(c: ZionContact): string[] {
  const out: string[] = []
  for (const raw of [
    c.contactId,
    c.sessionToken,
    c.nickname,
    c.customName,
    c.inboundQueueId,
  ]) {
    const k = String(raw || '')
      .trim()
      .toLowerCase()
    if (!k) continue
    out.push(k)
    if (k.startsWith('@')) out.push(k.slice(1))
    else out.push(`@${k}`)
  }
  return [...new Set(out)]
}

function contactMatchesPeer(c: ZionContact, variants: string[]): boolean {
  const keys = keysForContact(c)
  return variants.some((v) => keys.includes(v))
}

/** customName utente > nickname app — stessa logica della lista chat. */
export function notifyLabelFromContact(c: ZionContact): string | null {
  const custom = c.customName?.trim()
  if (custom && !isHexSessionId(custom)) return custom
  const nick = c.nickname?.trim()
  if (nick && !isHexSessionId(nick)) return nick
  return null
}

function pickDisplayName(row: ContactNameRow | undefined): string | null {
  if (!row) return null
  const custom = row.customName?.trim()
  if (custom && !isHexSessionId(custom)) return custom
  const display = row.name?.trim()
  if (display && !isHexSessionId(display) && display !== 'Contatto') return display
  const nick = row.nickname?.trim() || row.username?.trim()
  if (nick && !isHexSessionId(nick)) return nick
  return null
}

function buildContactRow(c: ZionContact, id: string, now: number): ContactNameRow {
  const display = displayForContact(c) || notifyLabelFromContact(c) || ''
  const username = c.nickname?.trim() || display
  return {
    id,
    name: display,
    customName: c.customName?.trim() || undefined,
    nickname: c.nickname?.trim() || undefined,
    sessionToken: c.sessionToken?.trim().toLowerCase() || undefined,
    username,
    updatedAt: now,
  }
}

/** Fonte primaria: zion-pairing (customName salvato dal rename utente). */
async function lookupFromPairingDbByHints(hints: string[]): Promise<string | null> {
  const variants = [...new Set(hints.flatMap((h) => peerKeyVariants(h)))]
  if (!variants.length) return null
  try {
    const db = await getPairingDb()
    const all = await db.getAll('contacts')
    for (const c of all) {
      if (!contactMatchesPeer(c, variants)) continue
      const label = notifyLabelFromContact(c) || displayForContact(c)
      if (label) return label
    }
    return null
  } catch {
    return null
  }
}

/** Cache Storage: mappa peer → nome leggibile dal SW anche a PWA chiusa. */
export async function persistNotifyNamesCache(contacts?: ZionContact[]): Promise<void> {
  const list = contacts ?? (await listContacts())
  const map: Record<string, string> = {}
  for (const c of list) {
    const label = notifyLabelFromContact(c) || displayForContact(c)
    if (!label) continue
    for (const id of keysForContact(c)) {
      map[id] = label
    }
  }
  try {
    if (typeof caches === 'undefined') return
    const origin =
      typeof location !== 'undefined' && location.origin ? location.origin : 'https://www.zionyx.ch'
    const cache = await caches.open(NOTIFY_NAMES_CACHE)
    const url = `${origin}${NOTIFY_NAMES_PATH}`
    let merged = map
    try {
      const prev = await cache.match(url)
      if (prev) {
        const oldMap = (await prev.json()) as Record<string, string>
        merged = { ...oldMap, ...map }
      }
    } catch {
      /* ignore */
    }
    await cache.put(
      url,
      new Response(JSON.stringify(merged), {
        headers: { 'Content-Type': 'application/json' },
      }),
    )
  } catch {
    /* ignore */
  }
}

/** Bootstrap veloce da localStorage mirror (customName) prima che IDB sia pronto. */
export async function persistNotifyNamesFromMirror(): Promise<void> {
  const mirrors = loadPairedContactsMirror()
  if (!mirrors.length) return
  const pseudo: ZionContact[] = mirrors.map((m) => ({
    contactId: m.contactId?.trim() || m.sessionToken.trim(),
    publicKey: m.publicKey?.trim() || '',
    nickname: m.nickname?.trim() || m.sessionToken.trim().slice(0, 8),
    pairedAt: m.pairedAt || Date.now(),
    sessionToken: m.sessionToken.trim(),
    inboundQueueId: m.inboundQueueId?.trim() || '',
    customName: m.customName,
  }))
  await persistNotifyNamesCache(pseudo)
}

async function lookupNotifyNameFromCacheByHints(hints: string[]): Promise<string | null> {
  const variants = [...new Set(hints.flatMap((h) => peerKeyVariants(h)))]
  if (!variants.length || typeof caches === 'undefined') return null
  try {
    const origin =
      typeof location !== 'undefined' && location.origin ? location.origin : 'https://www.zionyx.ch'
    const cache = await caches.open(NOTIFY_NAMES_CACHE)
    const res = await cache.match(`${origin}${NOTIFY_NAMES_PATH}`)
    if (!res) return null
    const map = (await res.json()) as Record<string, string>
    for (const v of variants) {
      const n = String(map[v] || '').trim()
      if (n && !isHexSessionId(n)) return n
    }
    return null
  } catch {
    return null
  }
}

/** Scrive mappa peerId → nome in IndexedDB leggibile dal Service Worker (upsert, no wipe globale). */
export async function persistContactsForServiceWorker(
  contacts?: ZionContact[],
): Promise<Record<string, string>> {
  const list = contacts ?? (await listContacts())
  const db = await getDb()
  const names: Record<string, string> = {}
  const now = Date.now()
  const keepIds = new Set<string>()

  const tx = db.transaction(STORE, 'readwrite')
  const store = tx.store

  for (const c of list) {
    const display = displayForContact(c) || notifyLabelFromContact(c)
    if (!display) continue
    for (const id of keysForContact(c)) {
      keepIds.add(id)
      names[id] = display
      await store.put(buildContactRow(c, id, now))
    }
  }

  const allKeys = await store.getAllKeys()
  for (const key of allKeys) {
    if (typeof key === 'string' && !keepIds.has(key)) {
      await store.delete(key)
    }
  }

  await tx.done
  await persistNotifyNamesCache(list)
  return names
}

/** Upsert atomico di UN solo contatto nello store SW (non tocca gli altri). */
export async function persistSingleContactForServiceWorker(c: ZionContact): Promise<void> {
  const display = displayForContact(c) || notifyLabelFromContact(c)
  if (!display) return
  const db = await getDb()
  const now = Date.now()
  const tx = db.transaction(STORE, 'readwrite')
  for (const id of keysForContact(c)) {
    await tx.store.put(buildContactRow(c, id, now))
  }
  await tx.done
  await persistNotifyNamesCache([c])
}

export async function lookupContactNameForSw(peerId: string): Promise<string | null> {
  return lookupContactNameForHints([peerId])
}

export async function lookupContactNameForHints(hints: string[]): Promise<string | null> {
  const uniq = [...new Set(hints.map((h) => String(h || '').trim()).filter(Boolean))]
  if (!uniq.length) return null

  const fromCache = await lookupNotifyNameFromCacheByHints(uniq)
  if (fromCache) return fromCache

  const fromPairing = await lookupFromPairingDbByHints(uniq)
  if (fromPairing) return fromPairing

  const variants = [...new Set(uniq.flatMap((h) => peerKeyVariants(h)))]
  if (!variants.length) return null
  try {
    const db = await getDb()
    for (const key of variants) {
      const row = await db.get(STORE, key)
      const name = pickDisplayName(row)
      if (name) return name
    }
    const all = await db.getAll(STORE)
    for (const row of all) {
      const rowKey = String(row.id || '')
        .trim()
        .toLowerCase()
      if (!rowKey) continue
      if (variants.some((v) => v === rowKey)) {
        const name = pickDisplayName(row)
        if (name) return name
      }
      const sess = String(row.sessionToken || '')
        .trim()
        .toLowerCase()
      if (sess && variants.includes(sess)) {
        const name = pickDisplayName(row)
        if (name) return name
      }
    }
    return null
  } catch {
    return null
  }
}

export async function lookupContactNameForPushData(
  data: Record<string, unknown> | null | undefined,
): Promise<string | null> {
  const hints = collectPeerHintsFromPushData(data)
  if (!hints.length) return null
  return lookupContactNameForHints(hints)
}
